import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { VaultConfirmService } from '../../../core/services/vault-confirm.service';

/**
 * The dialog behind `VaultConfirmService.ask`. Mounted once, at the app root.
 *
 * Shell copied from `add-document-modal.component.ts` — the spec names it the reference for new
 * vault dialogs — so Escape and body scroll-lock both work, and a backdrop click deliberately does
 * nothing. Not copied from the committee module's `confirm-dialog`, which has the same behaviours
 * but the committee palette.
 *
 * **Initial focus goes to Cancel.** Every use of this today is destructive, and a dialog that opens
 * with Enter already aimed at "Remove" is a trap for anyone who dismisses dialogs by reflex.
 */
@Component({
  selector: 'app-vault-confirm-dialog',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (confirm.request(); as request) {
      <div
        class="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-ink/70 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        [attr.aria-label]="request.title">

        <div class="bg-paper border border-rule rounded-xl w-full max-w-md flex flex-col sheet-in">

          <div class="p-6">
            <div class="flex items-start gap-4">
              @if (request.tone !== 'neutral') {
                <!--
                  Oxblood, spent here on purpose: this is the destructive moment the palette reserves
                  it for.
                -->
                <span
                  class="shrink-0 w-10 h-10 rounded-full grid place-items-center text-seal
                         bg-seal/10"
                  aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L14.7 3.9a2 2 0 00-3.4 0z" />
                    <path d="M12 9v4M12 17h.01" />
                  </svg>
                </span>
              }

              <div class="min-w-0 flex-1">
                <h2 class="vault-display text-xl">{{ request.title }}</h2>
                <p class="text-sm text-ink-soft mt-2">{{ request.body }}</p>
              </div>
            </div>

            <!--
              The thing itself, on its own sheet. "Remove this document?" is a question about a
              specific file, and naming it is what stops the wrong one being destroyed.
            -->
            @if (request.entity; as entity) {
              <div class="mt-5 border border-rule rounded-lg bg-cabinet px-4 py-3">
                <p class="font-medium truncate">{{ entity.name }}</p>
                @if (entity.meta) {
                  <p class="text-sm text-ink-soft mt-0.5">{{ entity.meta }}</p>
                }
              </div>
            }
          </div>

          <footer class="flex items-center justify-end gap-3 px-6 pb-6">
            <button
              #cancelButton
              type="button"
              (click)="settle(false)"
              class="tap-target px-4 py-2 text-sm border border-rule rounded-lg
                     hover:border-sage transition-colors">
              {{ request.cancelLabel ?? 'Cancel' }}
            </button>

            <button
              type="button"
              (click)="settle(true)"
              class="tap-target px-4 py-2 text-sm text-paper rounded-lg
                     hover:opacity-90 transition-opacity"
              [class.bg-seal]="request.tone !== 'neutral'"
              [class.bg-ink]="request.tone === 'neutral'">
              {{ request.confirmLabel }}
            </button>
          </footer>
        </div>
      </div>
    }
  `,
})
export class VaultConfirmDialogComponent implements OnDestroy {
  readonly confirm = inject(VaultConfirmService);

  @ViewChild('cancelButton') cancelButton?: ElementRef<HTMLButtonElement>;

  private previousBodyOverflow = '';
  private locked = false;

  constructor() {
    // The dialog is created and destroyed by a signal rather than by routing, so the lock is driven
    // from the same signal instead of from ngOnInit/ngOnDestroy.
    effect(() => {
      if (this.confirm.request()) {
        this.lockScroll();
        queueMicrotask(() => this.cancelButton?.nativeElement.focus());
      } else {
        this.unlockScroll();
      }
    });
  }

  ngOnDestroy(): void {
    this.unlockScroll();
  }

  /** Escape declines, like Cancel. Nothing destructive may happen by dismissing a dialog. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.confirm.request()) this.settle(false);
  }

  settle(confirmed: boolean): void {
    this.confirm.settle(confirmed);
  }

  private lockScroll(): void {
    if (this.locked) return;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.locked = true;
  }

  private unlockScroll(): void {
    if (!this.locked) return;
    document.body.style.overflow = this.previousBodyOverflow;
    this.locked = false;
  }
}
